import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { authFetch, thumbnailUrl, withFileAuth } from '../../utils/secureFiles';
import { useBackClose } from '../../utils/backStack';

/*
 * 사진·영상 크게 보기 (채팅 · 채널 파일 모아보기 · 관리자 화면 공통)
 *
 * sharedvideo 의 이미지 뷰어 방식을 참고해 다시 만들었다 (그쪽 코드는 건드리지 않음).
 *  - 앞/현재/뒤 세 칸짜리 트랙을 손가락·마우스로 끌면 그대로 따라온다. 끝에서는 고무줄처럼 버틴다.
 *  - 놓으면 끈 거리·속도에 맞춰 부드럽게 넘어가거나 제자리로 튕겨 돌아온다.
 *  - 사진은 작은 미리보기(640px)를 먼저 보여주고, 원본이 다 받아지고 디코딩된 뒤에 바꿔 끼운다
 *    → 느린 망에서도 빈 화면 없이 흐린 사진 → 선명한 사진으로 이어진다.
 *  - 앞뒤 사진은 미리 받아 둔다. 느린 망(2g·데이터 절약)이면 원본 미리 받기는 건너뛴다.
 *  - 아래 필름 띠에서 아무 사진이나 눌러 바로 이동.
 */

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const SPRING = 'cubic-bezier(0.34, 1.4, 0.64, 1)';
const EDGE_RESIST = 0.12;
const MAX_SCALE = 6;
const BLANK = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

const isVideoItem = (item) => String(item?.mimetype || '').startsWith('video/');
const slowNetwork = () => {
    const c = navigator.connection;
    return !!c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''));
};

// 한 번 원본까지 받아 디코딩한 사진 — 다시 열거나 앞뒤로 오갈 때 미리보기 단계를 건너뛴다
const decodedOriginals = new Set();

function FittedImage({ item, box, loadOriginal, active, zoomStyle, imgRef, onImageClick }) {
    const original = withFileAuth(item.fileUrl);
    const preview = thumbnailUrl(item.fileUrl);
    const [src, setSrc] = useState(() => (decodedOriginals.has(item.fileUrl) ? original : preview));
    const [shown, setShown] = useState(false);
    const [ratio, setRatio] = useState(() => (item.width && item.height ? item.width / item.height : null));

    // 원본을 뒤에서 받아 디코딩까지 끝낸 뒤 바꿔 끼운다 (바꾸는 순간 깜빡이거나 반쯤 그려진 사진이 보이지 않게)
    useEffect(() => {
        if (!loadOriginal || src === original) return undefined;
        let alive = true;
        const img = new Image();
        img.decoding = 'async';
        img.src = original;
        const swap = () => { if (!alive) return; decodedOriginals.add(item.fileUrl); setSrc(original); };
        const decoding = typeof img.decode === 'function' ? img.decode() : new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
        decoding.then(swap, () => { if (alive && img.complete && img.naturalWidth) swap(); });
        return () => { alive = false; };
    }, [loadOriginal, original, src, item.fileUrl]);

    const onLoad = (e) => {
        setShown(true);
        const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
        if (!ratio && w && h) setRatio(w / h);
    };
    // 미리보기를 만들 수 없는 파일이면 원본으로
    const onError = () => { if (src !== original) setSrc(original); else setShown(true); };

    // 미리보기(작은 사진)와 원본이 같은 크기로 보이도록 칸에 맞춘 크기를 직접 정한다
    let size;
    if (ratio && box.w && box.h) {
        const w = Math.min(box.w, box.h * ratio);
        size = { width: `${Math.round(w)}px`, height: `${Math.round(w / ratio)}px` };
    }
    const hd = src === original;
    return (
        <>
            <img
                ref={active ? imgRef : undefined}
                src={src || BLANK}
                alt={item.originalName || ''}
                decoding="async"
                draggable="false"
                className={`lb-img${hd ? '' : ' lb-preview'}${shown ? ' shown' : ''}`}
                style={{ ...size, ...(active ? zoomStyle : null) }}
                onLoad={onLoad}
                onError={onError}
                onClick={active ? onImageClick : undefined}
                onDragStart={e => e.preventDefault()}
            />
            {!shown && <span className="lb-spinner" aria-hidden="true" />}
            {shown && !hd && loadOriginal && active && <span className="lb-hd-pending" title="원본 받는 중" aria-hidden="true" />}
        </>
    );
}

function Slide({ item, active, box, lite, zoomStyle, imgRef, onImageClick }) {
    if (isVideoItem(item)) {
        if (active) {
            return <video key={item.fileUrl} className="lb-video" src={withFileAuth(item.fileUrl)} controls autoPlay playsInline preload="auto" />;
        }
        // 옆 칸 영상은 첫 장면만 (느린 망이면 그것도 받지 않는다)
        return (
            <div className="lb-video-side">
                {!lite && <video src={`${withFileAuth(item.fileUrl)}#t=0.1`} preload="metadata" muted playsInline />}
                <span>▶</span>
            </div>
        );
    }
    return <FittedImage item={item} box={box} loadOriginal={active || !lite} active={active} zoomStyle={zoomStyle} imgRef={imgRef} onImageClick={onImageClick} />;
}

export const Lightbox = ({ mediaItems, initialIndex = 0, onClose }) => {
    useBackClose(true, onClose);
    const count = mediaItems.length;
    const [index, setIndex] = useState(() => Math.max(0, Math.min(initialIndex, count - 1)));
    const indexRef = useRef(index);
    indexRef.current = index;
    const item = mediaItems[index];
    const isVideo = isVideoItem(item);
    const lite = useRef(slowNetwork()).current;

    const stageRef = useRef(null);
    const trackRef = useRef(null);
    const fitRef = useRef(null);
    const imgRef = useRef(null);
    const stripRef = useRef(null);
    const [box, setBox] = useState({ w: 0, h: 0 });
    const [zoom, setZoom] = useState({ scale: 1, panX: 0, panY: 0 });
    const zoomRef = useRef(zoom);
    zoomRef.current = zoom;
    const zoomed = zoom.scale > 1.02;
    const anim = useRef({ offset: 0, pendingDir: 0, queued: 0, id: 0, timer: null });
    const g = useRef({ mode: null });

    // ── 칸 크기 ────────────────────────────────────────────
    // 무대(stage) 크기가 바뀔 때마다 가운데 칸의 안쪽 크기를 다시 잰다 (가운데 칸 요소는 넘길 때마다 바뀌므로 무대를 관찰)
    useLayoutEffect(() => {
        const stage = stageRef.current;
        if (!stage) return undefined;
        const measure = () => {
            const el = fitRef.current;
            if (el) setBox(b => (b.w === el.clientWidth && b.h === el.clientHeight ? b : { w: el.clientWidth, h: el.clientHeight }));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(stage);
        return () => ro.disconnect();
    }, []);

    // ── 트랙 이동 ──────────────────────────────────────────
    const setTrack = useCallback((offsetPx, transition = 'none', base = -33.3333) => {
        const t = trackRef.current;
        if (!t) return;
        t.style.transition = transition;
        t.style.transform = offsetPx ? `translate3d(calc(${base}% + ${offsetPx}px), 0, 0)` : `translate3d(${base}%, 0, 0)`;
    }, []);

    // 칸이 바뀐 직후(그리기 전) 트랙을 가운데로 되돌린다 → 새 가운데 칸이 이미 제자리에 있어 이음새가 안 보인다
    useLayoutEffect(() => {
        anim.current.offset = 0;
        setTrack(0);
        setZoom({ scale: 1, panX: 0, panY: 0 });
    }, [index, setTrack]);

    const springBack = useCallback(() => {
        const a = anim.current;
        a.offset = 0;
        setTrack(0, `transform 0.3s ${SPRING}`);
    }, [setTrack]);

    const complete = useCallback((dir) => {
        const a = anim.current;
        if (a.pendingDir) { a.queued = dir; return; } // 넘어가는 중에 또 누르면 끝나자마자 이어서
        const target = indexRef.current + dir;
        if (target < 0 || target >= count) { springBack(); return; }
        a.pendingDir = dir;
        const myId = ++a.id;
        const width = stageRef.current?.clientWidth || window.innerWidth;
        const progress = Math.min(1, Math.abs(a.offset) / (width * 0.4));
        // 버튼·키보드는 빠르게, 끌어서 놓으면 남은 거리에 맞춰 (많이 끌었을수록 짧게)
        const duration = Math.abs(a.offset) > 1 ? 0.36 - progress * 0.12 : 0.24;
        setTrack(0, `transform ${duration}s ${EASE}`, dir > 0 ? -66.6667 : 0);
        const finish = () => {
            if (a.id !== myId || !a.pendingDir) return;
            clearTimeout(a.timer);
            a.pendingDir = 0;
            setIndex(i => Math.max(0, Math.min(count - 1, i + dir)));
            if (a.queued) { const q = a.queued; a.queued = 0; requestAnimationFrame(() => complete(q)); }
        };
        a.finish = finish;
        trackRef.current?.addEventListener('transitionend', finish, { once: true });
        a.timer = setTimeout(finish, duration * 1000 + 80); // transitionend 가 안 오는 경우 대비
    }, [count, springBack, setTrack]);

    const jumpTo = useCallback((i) => {
        const a = anim.current;
        a.pendingDir = 0; a.queued = 0; a.id++;
        clearTimeout(a.timer);
        setIndex(Math.max(0, Math.min(count - 1, i)));
    }, [count]);

    // ── 확대 ───────────────────────────────────────────────
    const clampPan = useCallback((scale, panX, panY) => {
        if (scale <= 1.02) return { panX: 0, panY: 0 };
        const img = imgRef.current;
        const w = img ? img.offsetWidth : box.w, h = img ? img.offsetHeight : box.h;
        const maxX = Math.max(0, (w * scale - box.w) / 2), maxY = Math.max(0, (h * scale - box.h) / 2);
        return { panX: Math.max(-maxX, Math.min(maxX, panX)), panY: Math.max(-maxY, Math.min(maxY, panY)) };
    }, [box]);

    const zoomAt = useCallback((clientX, clientY, nextScale) => {
        if (isVideo) return;
        setZoom(prev => {
            const scale = Math.max(1, Math.min(MAX_SCALE, nextScale));
            if (scale <= 1.02) return { scale: 1, panX: 0, panY: 0 };
            const rect = fitRef.current?.getBoundingClientRect();
            if (!rect) return { ...prev, scale };
            const px = clientX - (rect.left + rect.width / 2), py = clientY - (rect.top + rect.height / 2);
            const r = scale / prev.scale;
            return { scale, ...clampPan(scale, px - (px - prev.panX) * r, py - (py - prev.panY) * r) };
        });
    }, [clampPan, isVideo]);
    const resetZoom = useCallback(() => setZoom({ scale: 1, panX: 0, panY: 0 }), []);

    // ── 손가락·마우스 ─────────────────────────────────────
    const inVideoControls = (target, clientY) => {
        const v = target?.closest?.('video');
        if (!v) return false;
        return clientY > v.getBoundingClientRect().bottom - 64; // 재생 막대 근처는 영상 조작에 양보
    };

    const begin = (x, y, target, pointerType) => {
        // 넘어가는 중에 다시 잡으면 그 넘김은 즉시 끝낸다 (연속으로 휙휙 넘길 때)
        if (anim.current.pendingDir) { anim.current.queued = 0; anim.current.finish?.(); }
        const onImage = !!target?.closest?.('.lb-img');
        g.current = {
            mode: zoomRef.current.scale > 1.02 && onImage ? 'pan' : 'pending',
            startX: x, startY: y, lastX: x, lastT: performance.now(), velocity: 0,
            startPanX: zoomRef.current.panX, startPanY: zoomRef.current.panY,
            moved: false, onImage, pointerType,
            blank: !onImage && !target?.closest?.('video, .lb-video-side, button, a'),
        };
    };

    const move = (x, y, e) => {
        const s = g.current;
        if (!s.mode || s.mode === 'pinch') return;
        const dx = x - s.startX, dy = y - s.startY;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) s.moved = true;
        const now = performance.now();
        s.velocity = (x - s.lastX) / Math.max(1, now - s.lastT);
        s.lastX = x; s.lastT = now;
        if (s.mode === 'pending' && s.moved) {
            s.mode = Math.abs(dx) > Math.abs(dy) ? 'swipe' : 'vertical';
        }
        if (s.mode === 'swipe') {
            e?.preventDefault?.();
            const i = indexRef.current;
            const atEdge = (dx > 0 && i === 0) || (dx < 0 && i === count - 1);
            anim.current.offset = atEdge ? dx * EDGE_RESIST : dx;
            setTrack(anim.current.offset);
        } else if (s.mode === 'pan') {
            e?.preventDefault?.();
            setZoom(prev => ({ ...prev, ...clampPan(prev.scale, s.startPanX + dx, s.startPanY + dy) }));
        }
    };

    const end = (x, y) => {
        const s = g.current;
        const mode = s.mode;
        g.current = { mode: null, suppressClick: s.moved };
        if (mode === 'swipe') {
            const width = stageRef.current?.clientWidth || window.innerWidth;
            const dx = x - s.startX;
            const fling = Math.abs(s.velocity) > 0.45 && Math.abs(dx) > 24 && Math.sign(s.velocity) === Math.sign(dx);
            if (Math.abs(dx) > width * 0.18 || fling) complete(dx < 0 ? 1 : -1);
            else springBack();
            return;
        }
        if (mode === 'pan' && !s.moved) { resetZoom(); return; } // 확대된 사진을 톡 치면 원래 크기로
        if (mode === 'pending' && !s.moved) {
            if (s.onImage) {
                if (zoomRef.current.scale > 1.02) resetZoom(); else zoomAt(x, y, 2.5);
            } else if (s.blank) {
                onClose();
            }
        }
    };

    // 터치 (두 손가락 확대 포함)
    const pinch = useRef(null);
    const onTouchStart = (e) => {
        if (e.target.closest('button, a, .lb-strip')) return;
        if (e.touches.length === 2 && !isVideo) {
            const [a, b] = e.touches;
            pinch.current = {
                dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
                midX: (a.clientX + b.clientX) / 2, midY: (a.clientY + b.clientY) / 2,
                scale: zoomRef.current.scale, panX: zoomRef.current.panX, panY: zoomRef.current.panY,
            };
            g.current = { mode: 'pinch' };
            if (anim.current.offset) springBack();
            return;
        }
        if (e.touches.length !== 1) return;
        const t = e.touches[0];
        if (inVideoControls(e.target, t.clientY)) { g.current = { mode: null }; return; }
        begin(t.clientX, t.clientY, e.target, 'touch');
    };
    const onTouchMove = (e) => {
        if (g.current.mode === 'pinch' && e.touches.length === 2 && pinch.current) {
            e.preventDefault();
            const [a, b] = e.touches;
            const p = pinch.current;
            const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            const mid = { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
            const scale = Math.max(1, Math.min(MAX_SCALE, p.scale * (dist / Math.max(1, p.dist))));
            const r = scale / p.scale;
            setZoom({ scale: scale <= 1.02 ? 1 : scale, ...clampPan(scale, mid.x - p.midX + p.panX * r, mid.y - p.midY + p.panY * r) });
            return;
        }
        if (e.touches.length !== 1) return;
        move(e.touches[0].clientX, e.touches[0].clientY, e);
    };
    const onTouchEnd = (e) => {
        if (g.current.mode === 'pinch') {
            if (e.touches.length === 0) { g.current = { mode: null }; pinch.current = null; if (zoomRef.current.scale <= 1.02) resetZoom(); }
            return;
        }
        const t = e.changedTouches[0];
        if (t) end(t.clientX, t.clientY);
    };

    // 마우스·펜
    const onPointerDown = (e) => {
        if (e.pointerType === 'touch' || e.button !== 0) return;
        if (e.target.closest('button, a, .lb-strip')) return;
        if (inVideoControls(e.target, e.clientY)) return;
        begin(e.clientX, e.clientY, e.target, e.pointerType);
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => { if (e.pointerType !== 'touch' && g.current.mode) move(e.clientX, e.clientY, e); };
    const onPointerUp = (e) => { if (e.pointerType !== 'touch' && g.current.mode) end(e.clientX, e.clientY); };
    // 끌고 난 뒤 따라오는 click(영상 재생 토글 등)은 삼킨다
    const onClickCapture = (e) => {
        if (g.current.suppressClick) { g.current.suppressClick = false; e.preventDefault(); e.stopPropagation(); }
    };

    // Ctrl+휠 확대 / 트랙패드 좌우 밀기로 넘기기
    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return undefined;
        let wheelLock = 0;
        const onWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                if (isVideo) return;
                e.preventDefault();
                zoomAt(e.clientX, e.clientY, zoomRef.current.scale + (e.deltaY < 0 ? 0.35 : -0.35));
                return;
            }
            if (zoomRef.current.scale > 1.02 || Math.abs(e.deltaX) < 30 || Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
            e.preventDefault();
            const now = Date.now();
            if (now < wheelLock) return;
            wheelLock = now + 450;
            complete(e.deltaX > 0 ? 1 : -1);
        };
        stage.addEventListener('wheel', onWheel, { passive: false });
        return () => stage.removeEventListener('wheel', onWheel);
    }, [complete, isVideo, zoomAt]);

    // 키보드
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') complete(1);
            else if (e.key === 'ArrowLeft') complete(-1);
            else if (!isVideo && (e.key === '+' || e.key === '=')) zoomAt(window.innerWidth / 2, window.innerHeight / 2, zoomRef.current.scale + 0.5);
            else if (!isVideo && e.key === '-') zoomAt(window.innerWidth / 2, window.innerHeight / 2, zoomRef.current.scale - 0.5);
            else if (!isVideo && e.key === '0') resetZoom();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [complete, isVideo, onClose, resetZoom, zoomAt]);

    // 뒤 화면 스크롤 잠금
    useEffect(() => {
        const a = anim.current;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; clearTimeout(a.timer); };
    }, []);

    // 앞뒤 두 장까지 미리보기(작은 사진) 미리 받기
    useEffect(() => {
        if (lite) return;
        [2, -2].forEach(step => {
            const it = mediaItems[index + step];
            if (it && !isVideoItem(it)) { const img = new Image(); img.decoding = 'async'; img.src = thumbnailUrl(it.fileUrl); }
        });
    }, [index, lite, mediaItems]);

    // 필름 띠: 지금 사진이 가운데 오게
    useEffect(() => {
        const strip = stripRef.current;
        const cur = strip?.querySelector('.lb-thumb.current');
        if (!strip || !cur) return;
        strip.scrollTo({ left: cur.offsetLeft - strip.clientWidth / 2 + cur.offsetWidth / 2, behavior: 'smooth' });
    }, [index]);

    const download = async (e) => {
        e.stopPropagation();
        try {
            const response = await authFetch(item.downloadUrl || item.fileUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const url = URL.createObjectURL(await response.blob());
            const a = document.createElement('a');
            a.href = url; a.download = item.originalName || 'file';
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch { window.open(withFileAuth(item.fileUrl), '_blank', 'noopener'); }
    };

    const zoomStyle = { transform: `translate3d(${zoom.panX}px, ${zoom.panY}px, 0) scale(${zoom.scale})`, transition: g.current.mode === 'pinch' || g.current.mode === 'pan' ? 'none' : undefined };
    const onImageClick = (e) => e.stopPropagation(); // 확대·축소는 손 뗄 때(end) 처리

    return (
        <div className={`lightbox-overlay lb${count > 1 ? '' : ' single'}`} role="dialog" aria-modal="true" aria-label="사진 크게 보기">
            <div
                ref={stageRef}
                className={`lb-stage${zoomed ? ' zoomed' : ''}`}
                onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}
                onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
                onClickCapture={onClickCapture}
                onContextMenu={e => { if (!e.target.closest('video')) e.preventDefault(); }}
            >
                <div ref={trackRef} className="lb-track">
                    {[-1, 0, 1].map(rel => {
                        const i = index + rel;
                        const it = mediaItems[i];
                        return (
                            // 칸마다 "몇 번째 사진"을 열쇠로 둔다 → 넘길 때 이미 받아 둔 옆 칸 사진이 그대로 가운데로 옮겨 온다
                            <div key={it ? `m${i}` : `empty${rel}`} className="lb-slot">
                                <div className="lb-fit" ref={rel === 0 ? fitRef : undefined}>
                                    {it && <Slide item={it} active={rel === 0} box={box} lite={lite} zoomStyle={zoomStyle} imgRef={imgRef} onImageClick={onImageClick} />}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="lb-top">
                <span className="lb-name" title={item.originalName}>{item.originalName}</span>
                {count > 1 && <span className="lb-count">{index + 1} / {count}</span>}
                <button className="lb-btn" onClick={download} title="다운로드">💾</button>
                <button className="lb-btn lightbox-close" onClick={onClose} title="닫기 (Esc)">×</button>
            </div>

            {count > 1 && (
                <>
                    <button className="lb-nav prev" onClick={() => complete(-1)} disabled={index === 0} aria-label="이전">‹</button>
                    <button className="lb-nav next" onClick={() => complete(1)} disabled={index === count - 1} aria-label="다음">›</button>
                    <div className="lb-strip" ref={stripRef}>
                        {mediaItems.map((it, i) => (
                            <button key={`t${i}`} className={`lb-thumb${i === index ? ' current' : ''}`} onClick={() => jumpTo(i)} aria-label={`${i + 1}번째`}>
                                {isVideoItem(it) ? <span className="lb-thumb-video">▶</span> : <img src={thumbnailUrl(it.fileUrl)} alt="" loading="lazy" decoding="async" draggable="false" />}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
